import React, { useContext, useMemo, useState, useEffect } from "react";
import { useSearchParams, useParams, useNavigate } from "react-router-dom";
import { buildListRoute, parseRouteEntityId } from "../../utils";
import { gql, useQuery, useMutation } from "@apollo/client";
import StarekContext from "../../context/StarekContext";
import EntityLoadingState from "../../components/EntityLoadingState";
import EntityEmptyState from "../../components/EntityEmptyState";
import { EMPTY_MESSAGES } from "../../constants/emptyStateMessages";
import ConfirmModal from "../../components/modals/ConfirmModal";
import EntityOperationBar from "../../components/EntityOperationBar";
import MaintenanceSchedulesCalendarView from "./MaintenanceSchedulesCalendarView";
import MaintenanceSchedulesListingView from "./MaintenanceSchedulesListingView";
import MaintenanceScheduleSidebar from "./MaintenanceScheduleSidebar";

const UI_STRINGS = {
    PAGE_TITLE: "Maintenance Schedules",
    ENTITY_SINGULAR: "Maintenance Schedule",
    ENTITY_PLURAL: "Maintenance Schedules",
    DELETE_TITLE_SINGLE: (name) => `Delete Maintenance Schedule ${name}?`,
    DELETE_TITLE_BULK: (count) => `Delete ${count} Maintenance Schedules?`,
    DELETE_MESSAGE_SINGLE: (
        name,
    ) => `You are about to delete maintenance schedule "${name}".

This action cannot be undone.

Are you sure you wish to continue?`,
    DELETE_MESSAGE_BULK: (
        count,
    ) => `You are about to delete ${count} maintenance schedule${count !== 1 ? "s" : ""}.

This action cannot be undone.

Are you sure you wish to continue?`,
    DELETE_CONFIRM: "Delete",
    DELETE_CANCEL: "Cancel",
    TOTAL_SELECTED: (count) => `Total maintenance schedules selected: ${count}`,
};

const MaintenanceSchedules = (props) => {
    const starekContext = useContext(StarekContext);
    const navigate = useNavigate();
    const { id: routeId } = useParams();
    const routeScheduleId = parseRouteEntityId(routeId);
    const [searchParams, setSearchParams] = useSearchParams();
    const [viewMode, setViewMode] = useState("calendar");
    const [selectedRows, setSelectedRows] = useState([]);

    useEffect(() => {
        const viewParam = searchParams.get("view");
        if (viewParam && (viewParam === "calendar" || viewParam === "list")) {
            setViewMode(viewParam);
        }
    }, [searchParams]);
    const [filterText, setFilterText] = useState("");
    const [toggleCleared, setToggleCleared] = useState(false);
    const [showSidebar, setShowSidebar] = useState(false);
    const [routeEditSchedule, setRouteEditSchedule] = useState(null);
    const [confirmCfg, setConfirmCfg] = useState({ open: false });
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteDone, setDeleteDone] = useState(0);

    const GET_DEVICES_IN_PLANT = gql`
        query GetDevicesForPlant($where: ShadowDeviceWhereInput) {
            shadowDevices(where: $where) {
                id
                name
                maintenance_schedules {
                    id
                    name
                    description
                    schedule_type
                    schedule_attributes
                    created_at
                    updated_at
                    created_by {
                        id
                        name
                    }
                    updated_by {
                        id
                        name
                    }
                }
            }
        }
    `;

    const CREATE_ONE_MAINTENANCE_SCHEDULE = gql`
        mutation CreateOneMaintenanceSchedule(
            $data: MaintenanceScheduleCreateInput!
        ) {
            createOneMaintenanceSchedule(data: $data) {
                id
                name
                description
                schedule_type
                schedule_attributes
                device_id
            }
        }
    `;

    const UPDATE_ONE_MAINTENANCE_SCHEDULE = gql`
        mutation UpdateOneMaintenanceSchedule(
            $data: MaintenanceScheduleUpdateInput!
            $where: MaintenanceScheduleWhereUniqueInput!
        ) {
            updateOneMaintenanceSchedule(data: $data, where: $where) {
                id
                name
                description
                schedule_type
                schedule_attributes
                device_id
            }
        }
    `;

    const DELETE_ONE_MAINTENANCE_SCHEDULE = gql`
        mutation DeleteOneMaintenanceSchedule($id: Int!) {
            deleteOneMaintenanceSchedule(where: { id: $id }) {
                id
            }
        }
    `;

    const plantId = starekContext.lastUsedPlant?.id;
    const where = useMemo(
        () => (plantId ? { plant_id: { equals: Number(plantId) } } : undefined),
        [plantId],
    );

    const { data, loading, error, refetch } = useQuery(GET_DEVICES_IN_PLANT, {
        variables: { where },
        client: starekContext.engineClient,
        fetchPolicy: "network-only",
    });

    const [createMaintenanceSchedule] = useMutation(
        CREATE_ONE_MAINTENANCE_SCHEDULE,
        {
            client: starekContext.engineClient,
        },
    );
    const [updateMaintenanceSchedule] = useMutation(
        UPDATE_ONE_MAINTENANCE_SCHEDULE,
        {
            client: starekContext.engineClient,
        },
    );
    const [deleteOneMaintenanceSchedule] = useMutation(
        DELETE_ONE_MAINTENANCE_SCHEDULE,
        {
            client: starekContext.engineClient,
        },
    );

    const devices = data?.shadowDevices || [];

    useEffect(() => {
        if (!routeId) {
            setRouteEditSchedule(null);
            return;
        }

        if (loading) return;

        const listRoute = buildListRoute("/maintenance", searchParams);

        if (routeScheduleId == null) {
            starekContext.showError("Maintenance schedule not found");
            setRouteEditSchedule(null);
            navigate(listRoute, { replace: true });
            return;
        }

        // Find schedule with routeScheduleId across all devices
        let foundSchedule = null;
        let foundDeviceId = null;
        for (const device of devices) {
            const sch = (device.maintenance_schedules || []).find(
                (s) => Number(s.id) === routeScheduleId
            );
            if (sch) {
                foundSchedule = sch;
                foundDeviceId = device.id;
                break;
            }
        }

        if (!foundSchedule) {
            starekContext.showError("Maintenance schedule not found");
            setRouteEditSchedule(null);
            navigate(listRoute, { replace: true });
            return;
        }

        const scheduleWithDevice = {
            ...foundSchedule,
            device_id: foundDeviceId,
        };

        setRouteEditSchedule(scheduleWithDevice);
        setSelectedRows((current) =>
            current.length === 1 && Number(current[0]?.id) === routeScheduleId
                ? current
                : [scheduleWithDevice]
        );
        setShowSidebar(true);
    }, [
        routeId,
        routeScheduleId,
        loading,
        devices,
        searchParams,
        navigate,
        starekContext,
    ]);

    const handleRowClicked = (row) => {
        navigate(buildListRoute(`/maintenance/${row.scheduleId}`, searchParams));
    };

    const handleAdd = () => {
        setSelectedRows([]);
        setShowSidebar(true);
    };

    const handleEdit = () => {
        if (selectedRows.length !== 1) return;
        navigate(buildListRoute(`/maintenance/${selectedRows[0].id}`, searchParams));
    };

    const handleEditSchedule = (scheduleRow) => {
        navigate(buildListRoute(`/maintenance/${scheduleRow.scheduleId}`, searchParams));
    };

    const handleSidebarClose = async () => {
        setShowSidebar(false);
        setRouteEditSchedule(null);
        setSelectedRows([]);
        setToggleCleared(!toggleCleared);
        if (routeId) {
            navigate(buildListRoute("/maintenance", searchParams), {
                replace: true,
            });
        }
        await refetch();
    };

    const openConfirm = (cfg) => setConfirmCfg({ open: true, ...cfg });
    const closeConfirm = () => setConfirmCfg({ open: false });

    const handleSaveMaintenanceSchedule = async (schedulePayload) => {
        try {
            const currentUserId =
                starekContext?.currentUser?.id ||
                starekContext?.user?.id ||
                starekContext?.me?.id ||
                2;

            const scheduleAttributesObj = {
                from: schedulePayload.from,
                to: schedulePayload.to,
            };

            if (!schedulePayload.id) {
                // Create
                await createMaintenanceSchedule({
                    variables: {
                        data: {
                            schedule_type: 2,
                            name: schedulePayload.name,
                            description: schedulePayload.description || "",
                            schedule_attributes: JSON.stringify(
                                scheduleAttributesObj,
                            ),
                            plant: {
                                connect: {
                                    id: starekContext.lastUsedPlant.id,
                                },
                            },
                            device: {
                                connect: {
                                    id: schedulePayload.deviceId,
                                },
                            },
                            created_by: {
                                connect: { id: Number(currentUserId) },
                            },
                            updated_by: {
                                connect: { id: Number(currentUserId) },
                            },
                        },
                    },
                });
            } else {
                // Update
                await updateMaintenanceSchedule({
                    variables: {
                        where: { id: schedulePayload.id },
                        data: {
                            name: { set: schedulePayload.name },
                            description: {
                                set: schedulePayload.description || "",
                            },
                            device: {
                                connect: {
                                    id: schedulePayload.deviceId,
                                },
                            },
                            schedule_attributes: scheduleAttributesObj,
                            updated_by: {
                                connect: { id: Number(currentUserId) },
                            },
                        },
                    },
                });
            }

            await handleSidebarClose();
            starekContext.showSuccess(
                schedulePayload.id
                    ? `Maintenance Schedule "${schedulePayload.name}" updated successfully`
                    : `Maintenance Schedule "${schedulePayload.name}" created successfully`,
            );
        } catch (error) {
            starekContext.showError(error);
            throw error;
        }
    };

    const handleDeleteMaintenanceSchedule = async (schedule) => {
        try {
            await deleteOneMaintenanceSchedule({
                variables: { id: schedule.id },
            });
            await handleSidebarClose();
            starekContext.showSuccess(
                `Maintenance Schedule "${schedule.name}" deleted successfully`,
            );
        } catch (error) {
            starekContext.showError(error);
            throw error;
        }
    };

    const handleDelete = (schedules) => {
        if (!schedules || schedules.length === 0 || isDeleting) return;

        const isSingle = schedules.length === 1;
        const scheduleName =
            schedules[0]?.schedule?.name || schedules[0]?.name || "Unnamed";
        const title = isSingle
            ? UI_STRINGS.DELETE_TITLE_SINGLE(scheduleName)
            : UI_STRINGS.DELETE_TITLE_BULK(schedules.length);

        const message = isSingle
            ? UI_STRINGS.DELETE_MESSAGE_SINGLE(scheduleName)
            : UI_STRINGS.DELETE_MESSAGE_BULK(schedules.length);

        openConfirm({
            title: title,
            content: (
                <>
                    <div
                        className="text-dark"
                        style={{ whiteSpace: "pre-wrap" }}
                    >
                        {message}
                    </div>
                    <p className="mt-4 text-dark fw-bold">
                        {UI_STRINGS.TOTAL_SELECTED(schedules.length)}
                        &nbsp;
                        <span className="badge bg-info">
                            {schedules.length}
                        </span>
                    </p>
                </>
            ),
            confirmText: UI_STRINGS.DELETE_CONFIRM,
            cancelText: UI_STRINGS.DELETE_CANCEL,
            type: "danger",
            onConfirm: async () => {
                setIsDeleting(true);
                setDeleteDone(0);

                try {
                    for (const row of schedules) {
                        try {
                            const scheduleId = row.scheduleId || row.id;
                            await deleteOneMaintenanceSchedule({
                                variables: { id: scheduleId },
                            });
                        } catch (err) {
                            starekContext.showError(err);
                        } finally {
                            setDeleteDone((d) => d + 1);
                        }
                    }
                    await refetch();
                    setSelectedRows([]);
                    setToggleCleared(!toggleCleared);
                } finally {
                    setIsDeleting(false);
                    closeConfirm();
                }
            },
            onClose: closeConfirm,
        });
    };

    if (loading) return <EntityLoadingState icon="fa-cogs" />;

    return error ? (
        <div className="tw-text-center tw-mt-10 tw-text-red-600">
            Failed to load maintenance schedules: {error.message}
        </div>
    ) : (
        <div className="container-fluid">
            <div className="font-weight-bold mb-3">{UI_STRINGS.PAGE_TITLE}</div>

            <EntityOperationBar
                entity={{
                    singular: UI_STRINGS.ENTITY_SINGULAR,
                    plural: UI_STRINGS.ENTITY_PLURAL,
                }}
                allowSearch={true}
                allowAdd={true}
                allowDelete={true}
                allowEdit={true}
                defaultView={viewMode}
                selectedRows={selectedRows}
                isDeleting={isDeleting}
                deleteDone={deleteDone}
                onSearch={(text) => setFilterText(text)}
                onAdd={handleAdd}
                onViewChange={(mode) => {
                    setViewMode(mode);
                    setSearchParams({ view: mode });
                    setSelectedRows([]);
                }}
                onEdit={handleEdit}
                onDelete={() => handleDelete(selectedRows)}
            >
                <EntityOperationBar.View
                    mode="calendar"
                    icon="fa fa-calendar"
                    tooltip="Calendar View"
                >
                    <MaintenanceSchedulesCalendarView
                        devices={devices}
                        selectedRows={selectedRows}
                        onSelectedRowsChange={(state) =>
                            setSelectedRows(state.selectedRows)
                        }
                        onRefresh={refetch}
                        onEditSchedule={handleEditSchedule}
                        filterText={filterText}
                    />
                </EntityOperationBar.View>

                <EntityOperationBar.View
                    mode="list"
                    icon="fa fa-list"
                    tooltip="List View"
                >
                    <MaintenanceSchedulesListingView
                        items={devices}
                        selectedRows={selectedRows}
                        onSelectedRowsChange={(state) =>
                            setSelectedRows(state.selectedRows)
                        }
                        filterText={filterText}
                        toggleCleared={toggleCleared}
                        noDataComponent={<EntityEmptyState icon="fa-cogs" entityName="Maintenance Schedule" messages={EMPTY_MESSAGES.maintenanceSchedules} isFiltered={!!filterText.trim()} onAdd={handleAdd} />}
                        onRowClicked={handleRowClicked}
                    />
                </EntityOperationBar.View>
            </EntityOperationBar>

            <MaintenanceScheduleSidebar
                show={showSidebar}
                onDiscard={handleSidebarClose}
                item={routeEditSchedule || selectedRows[0] || null}
                plantId={starekContext.lastUsedPlant.id}
                devices={devices}
                onSave={handleSaveMaintenanceSchedule}
                onDelete={handleDeleteMaintenanceSchedule}
            />

            <ConfirmModal
                open={confirmCfg.open}
                title={confirmCfg.title}
                confirmText={confirmCfg.confirmText}
                cancelText={confirmCfg.cancelText}
                type={confirmCfg.type}
                loading={isDeleting}
                count={selectedRows.length}
                done={deleteDone}
                onConfirm={confirmCfg.onConfirm}
                onClose={confirmCfg.onClose}
            >
                {confirmCfg.content}
            </ConfirmModal>
        </div>
    );
};

export { MaintenanceSchedules as Maintenance };
